// Integrations — outbound telemetry forwarding config (v1: Azure Log Analytics /
// Sentinel). P1 SPINE: destination config + per-data-type toggles, each mutation
// audited server-side. There is NO egress yet — forwarding, Test connection, and
// live health arrive in later phases; the page says so honestly (never presents an
// unbuilt capability as working). Corenet/agate: antd + useToken + Lucide, no hex.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Collapse, Divider, Form, Input, List, Modal, Space, Switch, Table, Tag, Typography, message, theme } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Cable, ShieldCheck, Lock, PlugZap, Search, Activity } from "lucide-react";
import {
  integrationsApi,
  STREAM_META,
  statusTone,
  type AzureDCR,
  type AzureWorkspace,
  type EngineStatus,
  type Integration,
  type SinkConfig,
  type Stream,
  type TestResult,
} from "@/lib/api/integrations";

const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";

type FieldMeta = { key: keyof SinkConfig; label: string; ph: string; required?: boolean; help?: string };

// Fields split by JOB (design review): the SEND PATH (what makes data flow) is always
// visible; DISCOVERY + READ-BACK (used only to browse Azure and to verify the probe
// during Test connection) collapses into an advanced section.
const CONNECTION_FIELDS: FieldMeta[] = [
  { key: "ingestionEndpoint", label: "Ingestion endpoint", ph: "https://<dce-or-dcr>.<region>.ingest.monitor.azure.com", required: true,
    help: "The Logs Ingestion (DCE/DCR) URL telemetry is POSTed to." },
  { key: "dcrImmutableId", label: "DCR immutable id", ph: "dcr-xxxxxxxxxxxxxxxx", required: true,
    help: "Filled for you by Select DCR." },
  { key: "streamName", label: "Stream name", ph: "Custom-BlueRockAudit", required: true,
    help: "Must match the stream declared in the DCR." },
  { key: "tenantId", label: "Tenant id", ph: "<entra-tenant-guid>", required: true,
    help: "Entra tenant of the workspace; used to mint the managed-identity token." },
  { key: "identityClientId", label: "Managed identity clientId", ph: "<uami-client-guid>", required: true,
    help: "The dedicated telemetry user-assigned identity's clientId. Required — the host has more than one identity, so the token must name which one." },
];

// Read-back / discovery only — NOT on the send path. tableName is used solely by the
// Test-connection KQL read-back (the forwarder POSTs to endpoint/DCR/stream, never the
// table); subscription/resourceGroup exist only to enumerate for the pickers.
const ADVANCED_FIELDS: FieldMeta[] = [
  { key: "subscriptionId", label: "Subscription id", ph: "<subscription-guid>", help: "Used only to list DCRs and workspaces in the pickers." },
  { key: "resourceGroup", label: "Resource group", ph: "<rg-name>", help: "Used only to list DCRs and workspaces in the pickers." },
  { key: "workspaceId", label: "Workspace ID (customerId)", ph: "<workspace-customerId-guid>", help: "The GUID used in the Test-connection read-back KQL." },
  { key: "workspaceResourceId", label: "Workspace resource ID (ARM path)", ph: "/subscriptions/…/workspaces/…", help: "The full ARM path used to locate the workspace." },
  { key: "tableName", label: "Table name", ph: "BlueRockAudit_CL", help: "Log Analytics table the stream resolves to; read during Test connection." },
  { key: "region", label: "Region", ph: "eastus2", help: "Informational; the region is already embedded in the ingestion endpoint." },
];

const EMPTY: SinkConfig = {
  tenantId: "", subscriptionId: "", resourceGroup: "", region: "", workspaceResourceId: "",
  ingestionEndpoint: "", dcrImmutableId: "", streamName: "", tableName: "", identityClientId: "",
  workspaceId: "",
};

const tagColor = { success: "success", warning: "warning", danger: "error", default: "default" } as const;

export default function Integrations() {
  const { token } = theme.useToken();
  const [it, setIt] = useState<Integration | null>(null);
  const [live, setLive] = useState<EngineStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cfg, setCfg] = useState<SinkConfig>(EMPTY);
  const [displayName, setDisplayName] = useState("Microsoft Sentinel");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<{ key: string; text: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [picker, setPicker] = useState<null | "dcrs" | "workspaces">(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [dcrItems, setDcrItems] = useState<AzureDCR[]>([]);
  const [wsItems, setWsItems] = useState<AzureWorkspace[]>([]);

  const canBrowse = !!cfg.identityClientId && !!cfg.subscriptionId && !!cfg.resourceGroup;

  const openPicker = async (kind: "dcrs" | "workspaces") => {
    setPicker(kind);
    setPickerLoading(true);
    const q = { identityClientId: cfg.identityClientId, subscriptionId: cfg.subscriptionId, resourceGroup: cfg.resourceGroup };
    try {
      if (kind === "dcrs") setDcrItems(await integrationsApi.azure.dcrs(q));
      else setWsItems(await integrationsApi.azure.workspaces(q));
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Enumeration failed");
      setPicker(null);
    } finally {
      setPickerLoading(false);
    }
  };

  const pickDcr = (d: AzureDCR) => {
    setCfg((c) => ({ ...c, dcrImmutableId: d.immutable_id, ...(d.ingestion_endpoint ? { ingestionEndpoint: d.ingestion_endpoint } : {}) }));
    setPicker(null);
    if (!d.ingestion_endpoint) message.info("This DCR has no inline endpoint — pick a DCE endpoint or paste one.");
  };

  const pickWorkspace = (w: AzureWorkspace) => {
    setCfg((c) => ({ ...c, workspaceId: w.customer_id, workspaceResourceId: w.resource_id }));
    setPicker(null);
  };

  const reload = useCallback(async () => {
    try {
      const { integration: next, live: liveStatus } = await integrationsApi.get();
      setIt(next);
      setLive(liveStatus ?? null);
      setErr(null);
      if (next?.sink_config) setCfg({ ...EMPTY, ...next.sink_config });
      if (next?.display_name) setDisplayName(next.display_name);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load integrations");
    }
  }, []);

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await integrationsApi.test();
      setTestResult(res);
      if (res.outcome === "verified") message.success("Connection verified");
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Test connection failed");
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [reload]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await integrationsApi.putConfig(cfg, displayName.trim() || "Microsoft Sentinel");
      message.success("Destination saved");
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleStream = async (s: Stream, next: boolean) => {
    const meta = STREAM_META[s.stream_key];
    if (next && meta?.sensitive) {
      setConfirm({ key: s.stream_key, text: "" });
      return;
    }
    try {
      await integrationsApi.putStream(s.stream_key, { enabled: next });
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Update failed");
    }
  };

  const confirmSensitive = async () => {
    if (!confirm) return;
    try {
      await integrationsApi.putStream(confirm.key, { enabled: true, confirm: true });
      message.success(`${STREAM_META[confirm.key]?.label ?? confirm.key} enabled`);
      setConfirm(null);
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Enable failed");
    }
  };

  const columns: ColumnsType<Stream> = useMemo(
    () => [
      {
        title: "Data type",
        key: "type",
        render: (_, s) => {
          const m = STREAM_META[s.stream_key];
          return (
            <div>
              <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                {m?.label ?? s.stream_key}
                {m?.sensitive && (
                  <Tag color="warning" style={{ margin: 0 }}>
                    <Lock size={11} style={{ verticalAlign: "-1px", marginRight: 3 }} />
                    sensitive
                  </Tag>
                )}
              </div>
              <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{m?.desc}</div>
            </div>
          );
        },
      },
      {
        title: "Fields forwarded",
        key: "fields",
        width: 140,
        render: (_, s) =>
          s.field_allowlist?.length ? (
            <span style={{ fontFamily: MONO, fontSize: 12, color: token.colorTextSecondary }}>
              {s.field_allowlist.length}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: token.colorTextTertiary }}>—</span>
          ),
      },
      {
        title: "Forward",
        key: "toggle",
        width: 90,
        render: (_, s) => <Switch checked={s.enabled} onChange={(v) => toggleStream(s, v)} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token],
  );

  const status = it?.status ?? "unconfigured";
  const configured = status !== "unconfigured";

  // Endpoint format hint (Datadog pattern) — a soft warning, not a hard block.
  const endpointOff =
    !!cfg.ingestionEndpoint.trim() && !/^https:\/\/.+\.ingest\.monitor\.azure\.com/i.test(cfg.ingestionEndpoint.trim());
  // Derived preview of the exact URL the forwarder POSTs to (matches sentinel.go).
  const assembledUrl =
    cfg.ingestionEndpoint && cfg.dcrImmutableId && cfg.streamName
      ? `${cfg.ingestionEndpoint.trim().replace(/\/$/, "")}/dataCollectionRules/${cfg.dcrImmutableId}/streams/${cfg.streamName}?api-version=2023-01-01`
      : null;

  // Browse needs the discovery scope (clientId + subscription + RG). If it isn't set,
  // clicking a Select button expands the advanced section and points the user there,
  // instead of the dependency being invisible.
  const onSelect = (kind: "dcrs" | "workspaces") => {
    if (!canBrowse) {
      setAdvancedOpen(true);
      message.info("Set Managed identity clientId, Subscription id, and Resource group first (see Discovery & read-back).");
      return;
    }
    void openPicker(kind);
  };

  const renderField = (f: FieldMeta, selectBtn?: React.ReactNode) => {
    const input = (
      <Input
        value={cfg[f.key]}
        placeholder={f.ph}
        style={{ fontFamily: MONO, fontSize: 12 }}
        onChange={(e) => setCfg((c) => ({ ...c, [f.key]: e.target.value }))}
      />
    );
    return (
      <Form.Item
        key={f.key}
        label={f.label}
        required={f.required}
        tooltip={f.help}
        style={{ marginBottom: 12 }}
        validateStatus={f.key === "ingestionEndpoint" && endpointOff ? "warning" : undefined}
        help={f.key === "ingestionEndpoint" && endpointOff ? "Expected an https://…ingest.monitor.azure.com URL." : undefined}
      >
        {selectBtn ? (
          <Space.Compact style={{ width: "100%" }}>
            {input}
            {selectBtn}
          </Space.Compact>
        ) : (
          input
        )}
      </Form.Item>
    );
  };

  const selectDcrBtn = (
    <Button icon={<Search size={14} />} onClick={() => onSelect("dcrs")}>
      Select DCR
    </Button>
  );
  const selectWsBtn = (
    <Button icon={<Search size={14} />} onClick={() => onSelect("workspaces")}>
      Select workspace
    </Button>
  );

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "8px 0 48px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Cable size={22} color={token.colorPrimary} />
        <Typography.Title level={3} style={{ margin: 0 }}>
          Integrations
        </Typography.Title>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Forward this console's audit and enforcement telemetry into your own Microsoft Sentinel workspace,
        authenticated by the host's managed identity, with no shared secret.
      </Typography.Paragraph>

      {err && <Alert type="error" showIcon className="mb-3" message={err} style={{ marginBottom: 16 }} />}

      {/* status */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ShieldCheck size={18} color={token.colorPrimary} />
          <b>Microsoft Sentinel</b>
          <Tag color={tagColor[statusTone(status)]} style={{ marginLeft: "auto" }}>
            {status}
          </Tag>
          <Button
            icon={<PlugZap size={15} />}
            loading={testing}
            disabled={!configured}
            onClick={runTest}
          >
            Test connection
          </Button>
          <Switch
            checkedChildren="On"
            unCheckedChildren="Off"
            checked={!!it?.master_enabled}
            disabled={status !== "verified"}
            onChange={async (v) => {
              try {
                await integrationsApi.setMaster(v);
                await reload();
              } catch (e) {
                message.error(e instanceof Error ? e.message : "Failed");
              }
            }}
          />
        </div>
        {status !== "verified" && (
          <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 8 }}>
            Save a destination, then run Test connection. The connector can be turned on once a test verifies.
          </div>
        )}

        {/* Test connection outcome */}
        {testResult && (
          <Alert
            type={testResult.outcome === "verified" ? "success" : testResult.outcome === "error" ? "error" : "warning"}
            showIcon
            style={{ marginTop: 12 }}
            message={
              testResult.outcome === "verified"
                ? "Verified — a probe row was sent and read back"
                : testResult.outcome === "pending"
                  ? "Probe accepted — not yet visible (first ingestion can take 5–15 min). Re-test shortly."
                  : testResult.outcome === "accepted"
                    ? "Probe accepted — couldn't read it back automatically"
                    : "Connection test failed"
            }
            description={
              <div>
                <div>{testResult.detail}</div>
                {testResult.kql && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: token.colorTextTertiary }}>Run this in Log Analytics to confirm:</div>
                    <pre style={{ fontFamily: MONO, fontSize: 12, whiteSpace: "pre-wrap", margin: "4px 0 0" }}>{testResult.kql}</pre>
                  </div>
                )}
              </div>
            }
          />
        )}

        {/* live-effective forwarder health (once the engine has loaded a sink) */}
        {live?.loaded && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Activity size={14} color={token.colorTextTertiary} />
              <span style={{ fontSize: 12, fontWeight: 600, color: token.colorTextSecondary }}>Live forwarder</span>
              {live.breaker_open && <Tag color="error">breaker open</Tag>}
              {live.master_enabled && !live.config_matches_loaded && <Tag color="warning">pending deploy</Tag>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 20px", fontSize: 12, color: token.colorTextTertiary }}>
              <span>sent <b style={{ color: token.colorTextSecondary }}>{live.counters.Sent}</b></span>
              <span>dropped <b style={{ color: live.counters.Dropped ? token.colorWarning : token.colorTextSecondary }}>{live.counters.Dropped}</b></span>
              <span>failed <b style={{ color: live.counters.Failed ? token.colorError : token.colorTextSecondary }}>{live.counters.Failed}</b></span>
              <span>enforcement source: <b style={{ color: token.colorTextSecondary }}>{live.source_mode}</b>{live.source_mode !== "tail" && " (enforcement not live)"}</span>
              {live.loaded_endpoint && <span style={{ fontFamily: MONO }}>{live.loaded_endpoint}</span>}
            </div>
            {live.reconcile_error && (
              <div style={{ fontSize: 12, color: token.colorError, marginTop: 6 }}>reconcile: {live.reconcile_error}</div>
            )}
          </div>
        )}
      </Card>

      {/* destination */}
      <Card title="Destination" size="small" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: token.colorTextTertiary, marginBottom: 12 }}>
          Data is sent directly from this console to your Azure ingestion endpoint; it does not traverse BlueRock
          infrastructure. These are resource identifiers, not secrets.
        </div>
        <Form layout="vertical">
          <Form.Item label="Display name" style={{ marginBottom: 12 }}>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </Form.Item>

          {/* Connection — the send path (always visible) */}
          <Divider titlePlacement="start" style={{ margin: "4px 0 14px" }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: token.colorTextSecondary, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Connection
            </span>
          </Divider>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            {renderField(CONNECTION_FIELDS[0], selectDcrBtn)}
            {renderField(CONNECTION_FIELDS[1], selectDcrBtn)}
            {renderField(CONNECTION_FIELDS[2])}
            {renderField(CONNECTION_FIELDS[3])}
            {renderField(CONNECTION_FIELDS[4])}
          </div>
          {assembledUrl && (
            <div style={{ margin: "0 0 14px" }}>
              <div style={{ fontSize: 11, color: token.colorTextTertiary, marginBottom: 2 }}>Telemetry will POST to</div>
              <div style={{ fontFamily: MONO, fontSize: 11.5, color: token.colorTextSecondary, wordBreak: "break-all" }}>{assembledUrl}</div>
            </div>
          )}

          {/* Discovery & read-back — not on the send path (collapsed) */}
          <Collapse
            ghost
            activeKey={advancedOpen ? ["adv"] : []}
            onChange={(k) => setAdvancedOpen((Array.isArray(k) ? k : [k]).includes("adv"))}
            style={{ marginBottom: 8 }}
            items={[
              {
                key: "adv",
                label: <span style={{ fontSize: 13 }}>Discovery &amp; read-back <span style={{ color: token.colorTextTertiary }}>(optional)</span></span>,
                children: (
                  <>
                    <div style={{ fontSize: 12, color: token.colorTextTertiary, marginBottom: 12 }}>
                      Used to browse Azure for the values above and to read the probe row back during Test connection. Not
                      required to forward.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                      {renderField(ADVANCED_FIELDS[0])}
                      {renderField(ADVANCED_FIELDS[1])}
                      {renderField(ADVANCED_FIELDS[2], selectWsBtn)}
                      {renderField(ADVANCED_FIELDS[3], selectWsBtn)}
                      {renderField(ADVANCED_FIELDS[4])}
                      {renderField(ADVANCED_FIELDS[5])}
                    </div>
                  </>
                ),
              },
            ]}
          />

          <Button type="primary" loading={saving} onClick={saveConfig}>
            Save destination
          </Button>
        </Form>
      </Card>

      {/* ARM picker */}
      <Modal
        open={!!picker}
        title={picker === "workspaces" ? "Select a Log Analytics workspace" : "Select a data collection rule"}
        footer={null}
        onCancel={() => setPicker(null)}
        width={640}
      >
        {picker === "dcrs" ? (
          <List
            loading={pickerLoading}
            dataSource={dcrItems}
            locale={{ emptyText: "No DCRs found in this resource group" }}
            renderItem={(d) => (
              <List.Item actions={[<Button key="use" size="small" type="link" onClick={() => pickDcr(d)}>Use</Button>]}>
                <List.Item.Meta
                  title={<span style={{ fontFamily: MONO }}>{d.name}</span>}
                  description={
                    <span style={{ fontSize: 12, color: token.colorTextTertiary, fontFamily: MONO }}>
                      {d.immutable_id}{d.ingestion_endpoint ? ` · ${d.ingestion_endpoint}` : " · no inline endpoint (needs a DCE)"}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <List
            loading={pickerLoading}
            dataSource={wsItems}
            locale={{ emptyText: "No workspaces found in this resource group" }}
            renderItem={(w) => (
              <List.Item actions={[<Button key="use" size="small" type="link" onClick={() => pickWorkspace(w)}>Use</Button>]}>
                <List.Item.Meta
                  title={<span style={{ fontFamily: MONO }}>{w.name}</span>}
                  description={<span style={{ fontSize: 12, color: token.colorTextTertiary, fontFamily: MONO }}>{w.customer_id}</span>}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* data types */}
      <Card title="Data types" size="small">
        <div style={{ fontSize: 12, color: token.colorTextTertiary, marginBottom: 12 }}>
          Control exactly what leaves this console. Audit events and enforcement verdicts forward by default;
          everything else is opt-in. Each type forwards only its allow-listed fields.
        </div>
        <Table<Stream>
          rowKey="stream_key"
          size="small"
          pagination={false}
          dataSource={it?.streams ?? []}
          columns={columns}
        />
        {!configured && (
          <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 10 }}>
            Save a destination first; toggles persist once the integration exists.
          </div>
        )}
      </Card>

      {/* sensitive-enable confirm */}
      <Modal
        open={!!confirm}
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Lock size={16} color={token.colorWarning} /> Enable a sensitive data type
          </span>
        }
        okText="Enable"
        okButtonProps={{ danger: true, disabled: confirm?.text !== "ENABLE" }}
        onOk={confirmSensitive}
        onCancel={() => setConfirm(null)}
      >
        <p style={{ marginTop: 0 }}>
          <b>{confirm ? STREAM_META[confirm.key]?.label : ""}</b> can ship {confirm?.key === "prompt_body" ? "raw model input/output (source, secrets, personal data)" : "real developer identity"} to your SIEM. This is off by default. Type <b>ENABLE</b> to confirm.
        </p>
        <Input
          autoFocus
          placeholder="ENABLE"
          value={confirm?.text ?? ""}
          onChange={(e) => setConfirm((c) => (c ? { ...c, text: e.target.value } : c))}
          onPressEnter={() => confirm?.text === "ENABLE" && confirmSensitive()}
        />
      </Modal>
    </div>
  );
}
